import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PublicHeaderNav } from "@/components/PublicHeaderNav";

export type PublicHeaderAction =
  | { type: "link"; to: string; labelKey: string; variant?: "ghost" | "default" | "outline" | "secondary" }
  | { type: "anchor"; href: string; labelKey: string; variant?: "ghost" | "default" | "outline" | "secondary" }
  | { type: "node"; node: React.ReactNode };

interface Props {
  /** When true, renders a sticky translucent bar (default). */
  sticky?: boolean;
  /** Show "Features" link before language switcher (used on the marketing landing). */
  showFeaturesLink?: boolean;
  /** Show "Sign in" button after language switcher. Defaults to true. */
  showSignIn?: boolean;
  /** Show "For CFO · HR · IT" anchor that scrolls to the gatekeepers section (landing only). */
  showBuyersAnchor?: boolean;
  /** Optional extra action(s) appended to the right (e.g. "Contact sales"). */
  extraActions?: PublicHeaderAction[];
  /** Hide the logo entirely (e.g. on Auth where the logo lives in the hero). */
  hideLogo?: boolean;
  className?: string;
}

/**
 * Shared header for public marketing & auth surfaces.
 * Renders: [Logo] ── [optional Features link] [LanguageSwitcher] [Sign in] [extras]
 */
export function PublicHeader({
  sticky = true,
  showFeaturesLink = false,
  showSignIn = true,
  showBuyersAnchor = false,
  extraActions = [],
  hideLogo = false,
  className = "",
}: Props) {
  const { t } = useTranslation("ui");

  const wrapper = sticky
    ? "sticky top-0 z-50 bg-background/80 backdrop-blur border-b border-border/50"
    : "bg-background";

  return (
    <header className={`${wrapper} ${className}`}>
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3">
        {hideLogo ? (
          <span aria-hidden />
        ) : (
          <Link to="/" className="text-lg font-bold tracking-tight text-primary shrink-0">
            Relark
          </Link>
        )}
        {/* Primary navigation (desktop dropdown + mobile hamburger).
            Replaces the legacy showFeaturesLink / showBuyersAnchor props,
            which are now no-ops kept for backwards compatibility. */}
        <div className="flex-1 flex justify-end lg:justify-center">
          <PublicHeaderNav />
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <LanguageSwitcher variant="compact" />
          {showSignIn && (
            <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
              <Link to="/auth">{t("auth.signIn")}</Link>
            </Button>
          )}
          {extraActions.map((a, i) => {
            if (a.type === "node") return <span key={i}>{a.node}</span>;
            if (a.type === "anchor") {
              return (
                <Button key={i} variant={a.variant ?? "default"} size="sm" asChild>
                  <a href={a.href}>{t(a.labelKey)}</a>
                </Button>
              );
            }
            return (
              <Button key={i} variant={a.variant ?? "ghost"} size="sm" asChild>
                <Link to={a.to}>{t(a.labelKey)}</Link>
              </Button>
            );
          })}
        </div>
      </div>
    </header>
  );
}
